import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createClient, validateEmail, validateMetadataUrl, validateEntityId, testSSOConnection, type SSOTestResult } from '../../services/api/onboarding';
import StackSelectionStep, { type StackSelection } from './StackSelectionStep';
import StandardsSelectionStep, { type StandardsSelection } from './StandardsSelectionStep';
import { saveStackConfiguration } from '../../services/api/stackConfiguration';
import AccountCreationStep from './AccountCreationStep';
import PlanSelectionStep from './PlanSelectionStep';
import { useAuth } from '../../contexts/AuthContext';
import { useStatus } from '../../contexts/StatusProvider';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
}

interface TeamMember {
  email: string;
  role: 'admin' | 'user' | 'viewer';
}

const EnterpriseOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { Auth_Status, Email_Address } = useStatus();

  // Get assessment context from URL params (if coming from assessment page)
  const assessmentRepo = searchParams.get('repo');

  // If user is already authenticated (via StatusProvider), skip to step 3 (organization)
  const [currentStep, setCurrentStep] = useState(() => {
    if (Auth_Status) return 3; // Skip account creation and plan selection
    return 1; // Start with account creation
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);
  const [ssoTestResult, setSsoTestResult] = useState<SSOTestResult | null>(null);
  const [ssoTestLoading, setSsoTestLoading] = useState(false);
  const [showSsoTestModal, setShowSsoTestModal] = useState(false);

  // Account & Plan state - initialize with Email_Address if authenticated
  const [accountEmail, setAccountEmail] = useState(Email_Address || '');
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');

  const [formData, setFormData] = useState({
    // Organization
    organizationName: '',
    industry: '',
    teamSize: '',
    // SSO
    ssoProvider: '',
    ssoEntityId: '',
    ssoMetadataUrl: '',
    // Team
    teamMembers: [] as TeamMember[],
    newMemberEmail: '',
    newMemberRole: 'user' as 'admin' | 'user' | 'viewer',
    // Compliance
    gdprEnabled: false,
    ccpaEnabled: false,
    soc2Enabled: false,
    hipaaEnabled: false,
    // Features
    selectedFeatures: [] as string[]
  });

  // Stack and Standards selection state
  const [stackSelection, setStackSelection] = useState<StackSelection>({
    frontendStack: [],
    backendStack: [],
    cloudProvider: [],
    database: [],
    architecture: [],
    additionalTechnologies: ''
  });

  const [standardsSelection, setStandardsSelection] = useState<StandardsSelection>({
    selectedCategories: [],
    customStandards: ''
  });

  const steps: OnboardingStep[] = [
    { id: 1, title: 'Account', description: 'Create your account' },
    { id: 2, title: 'Plan', description: 'Select your plan' },
    { id: 3, title: 'Organization', description: 'Basic company information' },
    { id: 4, title: 'SSO Configuration', description: 'Single sign-on setup' },
    { id: 5, title: 'Team Provisioning', description: 'Add team members' },
    { id: 6, title: 'Compliance Settings', description: 'Enable compliance frameworks' },
    { id: 7, title: 'Technology Stack', description: 'Select your tech stack' },
    { id: 8, title: 'Standards Selection', description: 'Choose standards to follow' },
    { id: 9, title: 'Review & Launch', description: 'Confirm settings' }
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Manufacturing',
    'Retail',
    'Government',
    'Education',
    'Other'
  ];

  const ssoProviders = [
    { id: 'okta', name: 'Okta' },
    { id: 'azure', name: 'Azure AD' },
    { id: 'onelogin', name: 'OneLogin' },
    { id: 'google', name: 'Google Workspace' },
    { id: 'custom', name: 'Custom SAML' }
  ];

  const features = [
    'Multi-agent orchestration',
    'Event-driven architecture',
    'Cost analysis gates',
    'Automatic standards enforcement',
    'Parallel execution',
    'Privacy-by-design automation',
    'Dynamic structure discovery',
    'Cross-agent workflows',
    'Distributed state management',
    'Multi-environment security',
    'Audit trail systems',
    'Cost attribution',
    'Dynamic resource allocation',
    'Performance analytics',
    'Agent-aware testing',
    'Model selection optimization',
    'Deployment orchestration',
    'Custom workflows'
  ];

  const handleBack = () => {
    setSubmitError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addTeamMember = () => {
    if (formData.newMemberEmail && !formData.teamMembers.find(m => m.email === formData.newMemberEmail)) {
      setFormData({
        ...formData,
        teamMembers: [...formData.teamMembers, { email: formData.newMemberEmail, role: formData.newMemberRole }],
        newMemberEmail: '',
        newMemberRole: 'user'
      });
    }
  };

  const removeTeamMember = (email: string) => {
    setFormData({
      ...formData,
      teamMembers: formData.teamMembers.filter(m => m.email !== email)
    });
  };

  const toggleFeature = (feature: string) => {
    setFormData({
      ...formData,
      selectedFeatures: formData.selectedFeatures.includes(feature)
        ? formData.selectedFeatures.filter(f => f !== feature)
        : [...formData.selectedFeatures, feature]
    });
  };

  const handleTestSSOConnection = async () => {
    try {
      setSsoTestLoading(true);
      setSsoTestResult(null);

      const result = await testSSOConnection(
        formData.ssoEntityId,
        formData.ssoMetadataUrl
      );

      if (result.success && result.data.data?.Records?.[0]) {
        setSsoTestResult(result.data.data.Records[0]);
        setShowSsoTestModal(true);
      } else {
        setSubmitError(result.message || 'SSO connection test failed');
      }
    } catch (error) {
      console.error('SSO test error:', error);
      setSubmitError('An error occurred while testing SSO connection');
    } finally {
      setSsoTestLoading(false);
    }
  };

  const validateCurrentStep = (): { valid: boolean; error?: string } => {
    switch (currentStep) {
      case 1: // Account Creation - handled by AccountCreationStep component
        return { valid: Auth_Status };

      case 2: // Plan Selection
        if (!selectedPlan || selectedPlan === 'free') {
          return { valid: false, error: 'Please select a paid plan to continue' };
        }
        return { valid: true };

      case 3: // Organization
        if (!formData.organizationName.trim()) {
          return { valid: false, error: 'Organization name is required' };
        }
        if (!formData.industry) {
          return { valid: false, error: 'Please select an industry' };
        }
        if (!formData.teamSize) {
          return { valid: false, error: 'Please select team size' };
        }
        return { valid: true };

      case 4: // SSO Configuration
        // SSO is optional, but if provided, must be valid
        if (formData.ssoProvider) {
          if (!formData.ssoEntityId) {
            return { valid: false, error: 'Entity ID is required when SSO provider is selected' };
          }
          if (!validateEntityId(formData.ssoEntityId)) {
            return { valid: false, error: 'Invalid Entity ID format (must be URL or URN)' };
          }
          if (!formData.ssoMetadataUrl) {
            return { valid: false, error: 'Metadata URL is required when SSO provider is selected' };
          }
          if (!validateMetadataUrl(formData.ssoMetadataUrl)) {
            return { valid: false, error: 'Metadata URL must be a valid HTTPS URL' };
          }
        }
        return { valid: true };

      case 5: // Team Provisioning
        if (formData.teamMembers.length === 0) {
          return { valid: false, error: 'At least one admin team member is required' };
        }
        // Validate all team member emails
        for (const member of formData.teamMembers) {
          if (!validateEmail(member.email)) {
            return { valid: false, error: `Invalid email format: ${member.email}` };
          }
        }
        return { valid: true };

      case 6: // Compliance Settings
        // All optional, no validation needed
        return { valid: true };

      case 7: // Technology Stack
        // Optional but recommended
        return { valid: true };

      case 8: // Standards Selection
        // Optional
        return { valid: true };

      case 9: // Review
        // Final validation before submit
        return { valid: true };

      default:
        return { valid: true };
    }
  };

  const handleNext = () => {
    const validation = validateCurrentStep();
    if (!validation.valid) {
      setSubmitError(validation.error || 'Please complete all required fields');
      return;
    }
    setSubmitError(null);
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Final validation
      const validation = validateCurrentStep();
      if (!validation.valid) {
        setSubmitError(validation.error || 'Please complete all required fields');
        return;
      }

      // Additional validation for required fields
      if (!formData.organizationName.trim()) {
        setSubmitError('Organization name is required');
        return;
      }

      if (formData.teamMembers.length === 0) {
        setSubmitError('At least one admin team member is required');
        return;
      }

      console.log('Submitting enterprise onboarding:', { ...formData, selectedPlan });

      // Call API to create client
      const result = await createClient({ ...formData, selectedPlan });

      if (result.success && result.data.data?.Records?.[0]) {
        const onboarding = result.data.data.Records[0];
        console.log('Client onboarding successful:', onboarding);
        const clientId = onboarding.client.Client_ID;
        setCreatedClientId(clientId);

        // Save stack configuration if any selections were made
        if (stackSelection.frontendStack.length > 0 ||
            stackSelection.backendStack.length > 0 ||
            stackSelection.cloudProvider.length > 0 ||
            stackSelection.database.length > 0 ||
            stackSelection.architecture.length > 0) {
          try {
            await saveStackConfiguration(clientId, {
              Frontend_Stack: stackSelection.frontendStack,
              Backend_Stack: stackSelection.backendStack,
              Cloud_Provider: stackSelection.cloudProvider,
              Database_Technology: stackSelection.database,
              Architecture_Type: stackSelection.architecture,
              Selected_Standards: standardsSelection.selectedCategories,
              Additional_Technologies: stackSelection.additionalTechnologies,
              Custom_Standards: standardsSelection.customStandards
            });
            console.log('Stack configuration saved successfully');
          } catch (stackError) {
            console.warn('Failed to save stack configuration:', stackError);
            // Don't fail onboarding if stack config fails
          }
        }

        setSubmitSuccess(true);
        setSubmitError(null);
      } else {
        const errorMessage = result.message || 'Failed to create client. Please try again.';
        console.error('Client onboarding failed:', errorMessage);
        setSubmitError(errorMessage);
        setSubmitSuccess(false);
      }

    } catch (error) {
      console.error('Enterprise onboarding error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      );
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Success State */}
        {submitSuccess && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-4 text-2xl font-bold text-gray-900">Organization Created Successfully!</h2>
              <p className="mt-2 text-gray-600">
                Your enterprise account has been set up and is ready to use.
              </p>
              {createdClientId && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Client ID:</p>
                  <p className="text-lg font-mono font-semibold text-gray-900">{createdClientId}</p>
                </div>
              )}
              <div className="mt-6 space-y-2">
                <h3 className="font-semibold text-gray-900">Next Steps:</h3>
                <ul className="text-left text-gray-600 space-y-1 max-w-md mx-auto">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span>Admin user can now log in to the platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span>Team members will receive invitation emails</span>
                  </li>
                  {formData.ssoProvider && (
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span>SSO configuration is active</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span>Configure additional settings in your dashboard</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8">
                <button
                  onClick={() => window.location.href = '/'}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Form */}
        {!submitSuccess && (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Enterprise Onboarding</h1>
              <p className="text-gray-600 mt-2">Set up your organization in a few steps</p>
            </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between">
            {steps.map((step) => (
              <div key={step.id} className="flex-1 text-center">
                <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center ${
                  step.id < currentStep
                    ? 'bg-indigo-600 text-white'
                    : step.id === currentStep
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step.id < currentStep ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    step.id
                  )}
                </div>
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-900">{step.title}</div>
                  <div className="text-xs text-gray-500 hidden md:block">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Step 1: Account Creation */}
          {currentStep === 1 && (
            <AccountCreationStep
              onAccountCreated={(email) => {
                setAccountEmail(email);
                // Auto-add user as first team member
                if (!formData.teamMembers.find(m => m.email === email)) {
                  setFormData(prev => ({
                    ...prev,
                    teamMembers: [{ email, role: 'admin' as const }, ...prev.teamMembers]
                  }));
                }
                setCurrentStep(2); // Move to plan selection
              }}
              onSignInInstead={() => navigate('/login')}
            />
          )}

          {/* Step 2: Plan Selection */}
          {currentStep === 2 && (
            <PlanSelectionStep
              selectedPlan={selectedPlan}
              onPlanSelected={(planId) => {
                setSelectedPlan(planId);
                setCurrentStep(3); // Move to organization
              }}
              assessmentContext={assessmentRepo ? {
                repository: assessmentRepo
              } : undefined}
            />
          )}

          {/* Step 3: Organization */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Organization Information</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                >
                  <option value="">Select industry</option>
                  {industries.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size
                </label>
                <select
                  value={formData.teamSize}
                  onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                >
                  <option value="">Select team size</option>
                  <option value="1-10">1-10</option>
                  <option value="11-50">11-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-500">201-500</option>
                  <option value="500+">500+</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 4: SSO Configuration */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">SSO Configuration</h2>
              <p className="text-gray-600">Configure single sign-on for your organization</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SSO Provider
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {ssoProviders.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, ssoProvider: provider.id })}
                      className={`p-4 border rounded-lg text-center ${
                        formData.ssoProvider === provider.id
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {provider.name}
                    </button>
                  ))}
                </div>
              </div>
              {formData.ssoProvider && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entity ID
                    </label>
                    <input
                      type="text"
                      value={formData.ssoEntityId}
                      onChange={(e) => setFormData({ ...formData, ssoEntityId: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                      placeholder="https://your-org.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Metadata URL
                    </label>
                    <input
                      type="text"
                      value={formData.ssoMetadataUrl}
                      onChange={(e) => setFormData({ ...formData, ssoMetadataUrl: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                      placeholder="https://idp.example.com/metadata.xml"
                    />
                  </div>

                  {/* Test Connection Button */}
                  {formData.ssoEntityId && formData.ssoMetadataUrl && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleTestSSOConnection}
                        disabled={ssoTestLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {ssoTestLoading ? (
                          <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Test Connection
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Skip this step to use email/password authentication. You can configure SSO later.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Team Provisioning */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Team Provisioning</h2>
              <p className="text-gray-600">Invite team members to your organization</p>
              <div className="flex gap-4">
                <input
                  type="email"
                  value={formData.newMemberEmail}
                  onChange={(e) => setFormData({ ...formData, newMemberEmail: e.target.value })}
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  placeholder="team@example.com"
                />
                <select
                  value={formData.newMemberRole}
                  onChange={(e) => setFormData({ ...formData, newMemberRole: e.target.value as 'admin' | 'user' | 'viewer' })}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                >
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  type="button"
                  onClick={addTeamMember}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
              {formData.teamMembers.length > 0 && (
                <div className="border rounded-lg divide-y">
                  {formData.teamMembers.map((member) => (
                    <div key={member.email} className="flex items-center justify-between p-4">
                      <div>
                        <div className="font-medium text-gray-900">{member.email}</div>
                        <div className="text-sm text-gray-500">{member.role}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTeamMember(member.email)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Team members will receive an invitation email. You can add more members after setup.
                </p>
              </div>
            </div>
          )}

          {/* Step 6: Compliance Settings */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Compliance Settings</h2>
              <p className="text-gray-600">Enable compliance frameworks for your organization</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { key: 'gdprEnabled', name: 'GDPR', desc: 'EU General Data Protection Regulation' },
                  { key: 'ccpaEnabled', name: 'CCPA', desc: 'California Consumer Privacy Act' },
                  { key: 'soc2Enabled', name: 'SOC 2', desc: 'Service Organization Control 2' },
                  { key: 'hipaaEnabled', name: 'HIPAA', desc: 'Health Insurance Portability and Accountability Act' }
                ].map((framework) => (
                  <label
                    key={framework.key}
                    className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer ${
                      formData[framework.key as keyof typeof formData] ? 'border-indigo-600 bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData[framework.key as keyof typeof formData] as boolean}
                      onChange={(e) => setFormData({ ...formData, [framework.key]: e.target.checked })}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{framework.name}</div>
                      <div className="text-sm text-gray-500">{framework.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Select Features</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {features.map((feature) => (
                    <label
                      key={feature}
                      className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${
                        formData.selectedFeatures.includes(feature) ? 'border-indigo-600 bg-indigo-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedFeatures.includes(feature)}
                        onChange={() => toggleFeature(feature)}
                        className="sr-only"
                      />
                      <span className={formData.selectedFeatures.includes(feature) ? 'text-indigo-700' : 'text-gray-600'}>
                        {feature}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Technology Stack */}
          {currentStep === 7 && (
            <StackSelectionStep
              stackSelection={stackSelection}
              onChange={setStackSelection}
            />
          )}

          {/* Step 8: Standards Selection */}
          {currentStep === 8 && (
            <StandardsSelectionStep
              stackSelection={stackSelection}
              standardsSelection={standardsSelection}
              onChange={setStandardsSelection}
            />
          )}

          {/* Step 9: Review */}
          {currentStep === 9 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Review & Launch</h2>
              <p className="text-gray-600">Review your settings before launching</p>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Organization</h3>
                  <div className="text-sm text-gray-600">
                    <div>{formData.organizationName || 'Not set'}</div>
                    <div>{formData.industry || 'Industry not set'}</div>
                    <div>{formData.teamSize || 'Team size not set'}</div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">SSO</h3>
                  <div className="text-sm text-gray-600">
                    {formData.ssoProvider
                      ? `Provider: ${ssoProviders.find(p => p.id === formData.ssoProvider)?.name}`
                      : 'SSO not configured (using email/password)'}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Team Members</h3>
                  <div className="text-sm text-gray-600">
                    {formData.teamMembers.length > 0
                      ? `${formData.teamMembers.length} members invited`
                      : 'No members added yet'}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Compliance</h3>
                  <div className="text-sm text-gray-600">
                    {[
                      formData.gdprEnabled && 'GDPR',
                      formData.ccpaEnabled && 'CCPA',
                      formData.soc2Enabled && 'SOC 2',
                      formData.hipaaEnabled && 'HIPAA'
                    ].filter(Boolean).join(', ') || 'No frameworks enabled'}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Features</h3>
                  <div className="text-sm text-gray-600">
                    {formData.selectedFeatures.length > 0
                      ? `${formData.selectedFeatures.length} features selected`
                      : 'All features enabled (default)'}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Technology Stack</h3>
                  <div className="text-sm text-gray-600">
                    {stackSelection.frontendStack.length > 0 && (
                      <div className="mb-1">
                        <strong>Frontend:</strong> {stackSelection.frontendStack.join(', ')}
                      </div>
                    )}
                    {stackSelection.backendStack.length > 0 && (
                      <div className="mb-1">
                        <strong>Backend:</strong> {stackSelection.backendStack.join(', ')}
                      </div>
                    )}
                    {stackSelection.cloudProvider.length > 0 && (
                      <div className="mb-1">
                        <strong>Cloud:</strong> {stackSelection.cloudProvider.join(', ')}
                      </div>
                    )}
                    {stackSelection.database.length > 0 && (
                      <div className="mb-1">
                        <strong>Database:</strong> {stackSelection.database.join(', ')}
                      </div>
                    )}
                    {stackSelection.architecture.length > 0 && (
                      <div className="mb-1">
                        <strong>Architecture:</strong> {stackSelection.architecture.join(', ')}
                      </div>
                    )}
                    {stackSelection.frontendStack.length === 0 &&
                     stackSelection.backendStack.length === 0 &&
                     stackSelection.cloudProvider.length === 0 &&
                     stackSelection.database.length === 0 &&
                     stackSelection.architecture.length === 0 && (
                      <span>No stack selections</span>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Standards</h3>
                  <div className="text-sm text-gray-600">
                    {standardsSelection.selectedCategories.length > 0
                      ? `${standardsSelection.selectedCategories.length} standards categories selected`
                      : 'No standards selected'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {submitError && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="mt-1 text-sm text-red-700">{submitError}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    type="button"
                    onClick={() => setSubmitError(null)}
                    className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1 || isSubmitting}
              className={`px-6 py-2 border rounded-lg ${
                currentStep === 1 || isSubmitting
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Back
            </button>
            {currentStep < steps.length ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Organization...
                  </>
                ) : (
                  'Launch Organization'
                )}
              </button>
            )}
          </div>
        </div>
        </>
        )}

        {/* SSO Test Results Modal */}
        {showSsoTestModal && ssoTestResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  SSO Connection Test Results
                </h2>
                <button
                  onClick={() => setShowSsoTestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Overall Status */}
              <div className={`mb-6 p-4 rounded-lg ${ssoTestResult.is_valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-3">
                  {ssoTestResult.is_valid ? (
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  <div>
                    <h3 className={`text-lg font-semibold ${ssoTestResult.is_valid ? 'text-green-900' : 'text-yellow-900'}`}>
                      {ssoTestResult.is_valid ? 'All Tests Passed!' : 'Some Tests Failed'}
                    </h3>
                    <p className={`text-sm ${ssoTestResult.is_valid ? 'text-green-700' : 'text-yellow-700'}`}>
                      {ssoTestResult.tests_passed} of {ssoTestResult.tests_total} tests passed
                    </p>
                  </div>
                </div>
              </div>

              {/* Test Details */}
              <div className="space-y-3 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Test Details</h3>
                {ssoTestResult.test_details.map((test, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      test.status === 'PASS'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {test.status === 'PASS' ? (
                        <svg className="w-5 h-5 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{test.test}</div>
                        <div className={`text-sm ${test.status === 'PASS' ? 'text-green-700' : 'text-red-700'}`}>
                          {test.message}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Connection Info */}
              {ssoTestResult.metadata_parsed && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <h3 className="font-semibold text-gray-900 mb-2">Connection Information</h3>
                  {ssoTestResult.issuer && (
                    <div className="text-sm">
                      <span className="text-gray-600">Issuer:</span>
                      <span className="ml-2 text-gray-900 font-mono">{ssoTestResult.issuer}</span>
                    </div>
                  )}
                  {ssoTestResult.sso_url && (
                    <div className="text-sm">
                      <span className="text-gray-600">SSO URL:</span>
                      <span className="ml-2 text-gray-900 font-mono break-all">{ssoTestResult.sso_url}</span>
                    </div>
                  )}
                  {ssoTestResult.certificate_expiry && (
                    <div className="text-sm">
                      <span className="text-gray-600">Certificate Expiry:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(ssoTestResult.certificate_expiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowSsoTestModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {ssoTestResult.is_valid && (
                  <button
                    onClick={() => {
                      setShowSsoTestModal(false);
                      handleNext();
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Continue to Next Step
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterpriseOnboarding;
